import React, { FC } from 'react';
import { TypeLandingPageHeroFields } from '@/core/generated-types';
import { waveBorderSrc } from '@/components/common/LandingPageHero/borders/wave-border';
import { CONTENT_TYPE_LANDING_PAGE_HERO } from '@/core/constants';
import {
  getMediaComponent,
  isMuxVideo,
  isVideo,
} from '@/components/common/LandingPageHero/utils';
import { UniversalCta } from '../../UniversalCta';
import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';

const fullImageClasses =
  'flex h-[240px] items-center justify-center bg-cover bg-center lg:h-[448px] w-full';

export const FullImage: FC<TypeLandingPageHeroFields> = ({
  entryName,
  media,
  subHeaderText,
  headerText,
  subtext,
  cta,
  textBelowCta,
  version,
}) => {
  const isMediaVideo = isMuxVideo(media) || isVideo(media);
  const isWaveBorderVersion =
    version === CONTENT_TYPE_LANDING_PAGE_HERO.FULL_IMAGE_W_WAVE;
  return (
    <section
      data-testid={`landing-page-hero-${entryName}`}
      className={`mx-auto text-center ${isWaveBorderVersion ? 'bg-teal' : ''}`}
    >
      <div className="relative">
        {/*
          Note: next/image forces images with `fill` attribute to be absolute.
          Because of this, we need a variable wrapper to accommodate both video and image.
         */}
        {isMediaVideo ? (
          getMediaComponent({
            media,
            classes: fullImageClasses,
            autoplay: true,
          })
        ) : (
          <div className={fullImageClasses}>{getMediaComponent({ media })}</div>
        )}
        {isWaveBorderVersion && (
          <div
            data-testid="wave-border"
            className="absolute bottom-0 h-full w-full bg-contain bg-bottom bg-no-repeat xl:bg-auto xl:bg-repeat-x"
            style={{
              backgroundImage: `url('${waveBorderSrc}')`,
            }}
          ></div>
        )}
      </div>
      {subHeaderText && (
        <HeaderText
          text={subHeaderText}
          size={HeaderSize.H3}
          align="center"
          classes="mb-3"
        />
      )}
      {headerText && (
        <div className="px-8">
          <h1 className="mb-2 text-h-x3 font-bold lg:text-hs-x2">
            {headerText}
          </h1>
          <div className="mx-auto text-h-x6 lg:w-1/2 lg:text-d-lg">
            {subtext}
          </div>
        </div>
      )}
      {cta && (
        <div className="mt-6 px-8">
          <UniversalCta link={cta} />
        </div>
      )}
      {textBelowCta && (
        <div className="my-4 text-base text-gray-600">{textBelowCta}</div>
      )}
    </section>
  );
};
